import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSelectedCompany } from "./SelectedCompanyContext";
import { useUsers } from "./UsersContext";
import * as CompanyApi from "../Api/CompanyApi";
const COMPANY_NAMES_BY_ID = {
  "bURcnu5CfHkQD1ecbApJ": "As Oy Tuusulan Metsänkuningas",
  "4qfLcy6U0f9AaNSCsjpT": "As Oy Tuusulan Mesikämmen",
  "L2u5aObdJMDDcEqWIf7Z": "Puistokulma-Säätiö sr",
};

const CompanyContext = createContext(null);

export const CompanyProvider = ({ children }) => {
  const { selectedCompanyId } = useSelectedCompany();
  const { user } = useUsers();

  // 🔹 Cache: { [companyId]: baseInfo }
  const [companyCache, setCompanyCache] = useState({});

  // 🔹 Aktiivinen company
  const [companyBaseInfo, setCompanyBaseInfo] = useState(null);

  // 🔹 Status
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [error, setError] = useState(null);

  /* ============================================================================
     Reagoi company-valintaan
     - käyttää cachea jos mahdollista
     - hakee API:sta vain kerran per companyId
  ============================================================================ */
  useEffect(() => {
    if (!selectedCompanyId) {
      setCompanyBaseInfo(null);
      return;
    }

    // ✅ 1. Cache-hit → ei API-kutsua
    if (companyCache[selectedCompanyId]) {
      setCompanyBaseInfo(companyCache[selectedCompanyId]);
      return;
    }

    // 🔁 2. Cache-miss → hae API:sta
    let cancelled = false;
    setLoadingCompany(true);
    setError(null);

    CompanyApi.getCompanyBaseInfo(selectedCompanyId)
      .then((data) => {
        if (cancelled) return;

        setCompanyCache((prev) => ({
          ...prev,
          [selectedCompanyId]: data,
        }));

        setCompanyBaseInfo(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("getCompanyBaseInfo failed:", err);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCompany(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, companyCache]);

  /* ============================================================================
     Johdettu tieto (frontend-normalisointi)
  ============================================================================ */

  const companyName = useMemo(() => {
    return user?.companyNames?.[selectedCompanyId] || "";
  }, [user, selectedCompanyId]);

  const companyNamesById = useMemo(() => {
    return COMPANY_NAMES_BY_ID;
  }, []);

  const companyColor = useMemo(() => {
    return (
      companyBaseInfo?.oletusväri ||
      "#286e34"
    );
  }, [companyBaseInfo]);

  /* ============================================================================
   🎨 Apply company theme to CSS variables (GLOBAL)
  ============================================================================ */

  useEffect(() => {
    if (!companyColor) 
      return;

    const root = document.documentElement;
    // Pääväri
    root.style.setProperty("--company-color", companyColor);
    // Johdetut sävyt (valinnainen mutta suositeltava)
    root.style.setProperty(
      "--company-color-soft",
      `color-mix(in srgb, ${companyColor} 10%, transparent)`
    );
    root.style.setProperty(
      "--company-color-strong",
      `color-mix(in srgb, ${companyColor} 85%, black)`
    );
  }, [companyColor]);

  /* ============================================================================
     Context value
  ============================================================================ */
  const value = {
    // raw
    companyBaseInfo,
    loadingCompany,
    error,

    // derived
    companyName,
    companyColor,

    // cache (hyödyllinen myöhemmin)
    companyCache,
    companyNamesById,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    throw new Error("useCompany must be used inside CompanyProvider");
  }
  return ctx;
};
